import React from 'react';
import { motion } from 'framer-motion';
import { Award, Zap, CheckCircle2, Gift } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useRestaurantData } from '../hooks/useRestaurantData';
import './LoyaltySection.css';

const LoyaltySection = () => {
    const { userPoints } = useCart();
    const data = useRestaurantData();
    const loyalty = data.restaurant.loyalty || { rewardThreshold: 100, rewardText: 'Un burger offert !' };

    const progress = Math.min((userPoints / loyalty.rewardThreshold) * 100, 100);
    const pointsLeft = Math.max(loyalty.rewardThreshold - userPoints, 0);

    return (
        <section className="loyalty-section">
            <div className="container">
                <div className="loyalty-grid">
                    {/* Visual Card */}
                    <motion.div
                        initial={{ opacity: 0, rotateY: -20, x: -50 }}
                        whileInView={{ opacity: 1, rotateY: 0, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="loyalty-card-visual"
                    >
                        <div className="card-top">
                            <div className="card-brand">
                                <Award className="text-primary" size={28} />
                                <span>FIDÉLITÉ SUGAR HUT</span>
                            </div>
                            <div className="card-chip" />
                        </div>

                        <div className="card-points-display">
                            <span className="points-number">{userPoints}</span>
                            <span className="points-label">Points Cumulés</span>
                        </div>

                        <div className="card-bottom">
                            <div className="card-holder">CLIENT PREMIUM</div>
                            <Zap className="text-primary" size={20} fill="currentColor" />
                        </div>
                    </motion.div>

                    {/* Reward Info */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="reward-info-box"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-primary/10 rounded-2xl">
                                <Gift className="text-primary" size={32} />
                            </div>
                            <h2 className="text-3xl font-black">Mon Espace Récompenses</h2>
                        </div>

                        <p className="text-gray-500 mb-8">
                            Chaque commande chez Sugar Hut vous rapproche de votre prochain burger gratuit.
                            Gagnez <strong>{data.restaurant.loyalty?.pointsPerEuro || 1} point</strong> pour chaque euro dépensé !
                        </p>

                        <div className="progress-container">
                            <div className="flex justify-between items-end mb-2">
                                <span className="text-sm font-black uppercase text-gray-400">Progression</span>
                                <span className="text-xl font-bold">{userPoints} / {loyalty.rewardThreshold}</span>
                            </div>

                            <div className="progress-bar-bg">
                                <motion.div
                                    initial={{ width: 0 }}
                                    whileInView={{ width: `${progress}%` }}
                                    transition={{ duration: 1.5, delay: 0.5, ease: "easeOut" }}
                                    className="progress-bar-fill"
                                />
                            </div>

                            {progress >= 100 ? (
                                <div className="status-check mt-4 animate-bounce">
                                    <CheckCircle2 size={24} />
                                    <span>RÉCOMPENSE DÉBLOQUÉE !</span>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 mt-2">
                                    Encore <strong>{pointsLeft} points</strong> pour votre cadeau.
                                </p>
                            )}
                        </div>

                        <div className="mt-10 p-6 bg-gray/10 rounded-2xl border border-dashed border-gray">
                            <h4 className="text-xs font-black uppercase tracking-tighter text-gray-400 mb-2">Récompense Actuelle</h4>
                            <p className="reward-text text-xl italic">{loyalty.rewardText}</p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default LoyaltySection;
